import { useMemo, useRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select, { components } from 'react-select';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import ErrorRender from '../ErrorRender/ErrorRender';

const MENU_Z_INDEX = 10050;

const customStyles = ({ selectGroup }) => ({
  control: (provided, state) => ({
    ...provided,
    borderTopRightRadius: selectGroup ? '0' : '12px',
    borderTopLeftRadius: '12px',
    borderBottomRightRadius: selectGroup ? '0' : '12px',
    borderBottomLeftRadius: '12px',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
    borderColor: state.isFocused ? '#3eb7e9b3' : '#ced4da',
    fontSize: 'var(--font-label)',
    color: '#6c757d',
    '&:hover': {
      borderColor: 'none'
    }
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: MENU_Z_INDEX
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: MENU_Z_INDEX
  }),
  option: (provided, state) => ({
    ...provided,
    fontWeight: (state.isSelected || state.isFocused) && 'bold',
    backgroundColor: state.isFocused ? 'var(--tempedge-blue)' : 'inherit',
    color: state.isFocused ? 'white' : state.isSelected ? 'var(--tempedge-blue)' : 'gray'
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '190px'
  }),
  indicatorSeparator: () => null,
  placeholder: (provided) => ({
    ...provided,
    fontSize: 'var(--font-body)'
  })
});

const Option = (props) => {
  const { valueField, testIdPrefix } = props.selectProps;
  const optionValue = props.data?.[valueField];
  const optionTestId = optionValue
    ? `${testIdPrefix}-option-${String(optionValue)
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '')}`
    : `${testIdPrefix}-option`;

  return (
    <components.Option {...props} innerProps={{ ...props.innerProps, 'data-testid': optionTestId }}>
      {props.children}
    </components.Option>
  );
};

const Control = (props) => {
  const { testIdPrefix } = props.selectProps;
  return (
    <components.Control {...props} innerProps={{ ...props.innerProps, 'data-testid': `${testIdPrefix}-select` }}>
      {props.children}
    </components.Control>
  );
};

const selectComponents = { Option, Control };

const getOptionText = (option, data, textField, valueField, resolveFromData) => {
  if (!resolveFromData && option?.[textField]) {
    return option[textField];
  }

  const matched = data?.find((item) => item[valueField] === option?.[valueField]);
  return matched?.[textField] ?? option?.[textField] ?? '';
};

const DropdownList = ({
  input,
  meta,
  data,
  valueField,
  textField,
  value,
  onChange,
  disabled,
  isInvalid,
  limit = 30,
  defaultValue,
  placeholder: customPlaceholder,
  selectGroup = false,
  menuPosition = 'absolute',
  isSearchable,
  resolveFromData = false,
  'data-testid': testId
}) => {
  const { tempedgeTranslator } = useTempEdgeTranslator();
  const placeholder = customPlaceholder || tempedgeTranslator('com.tempedge.msg.label.select');
  const lastSelectedRef = useRef(undefined);
  const testIdPrefix = testId || 'dropdown';
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;

  const errorClass =
    isInvalid || (meta?.error && meta?.touched) ? 'tempEdge-dropdown-input-box has-error-dropdown' : 'tempEdge-dropdown-input-box';

  const rawValue = input ? input?.value : value;
  const valueSelected = rawValue === '' || rawValue === undefined ? null : rawValue;

  const getOptionLabel = (option) => String(getOptionText(option, data, textField, valueField, resolveFromData) || '');

  const formatOptionLabel = (option) => {
    const optionSelected = getOptionText(option, data, textField, valueField, resolveFromData);
    if (!optionSelected) return '';

    if (optionSelected.length > limit) {
      const displayText = `${optionSelected.slice(0, limit)}...`;
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${option[valueField]}`} className="font-label">
              {optionSelected}
            </Tooltip>
          }
        >
          <span>{displayText}</span>
        </OverlayTrigger>
      );
    }

    return optionSelected;
  };

  const filterOption = (option, inputValue) => {
    if (!inputValue) return true;

    const searchValue = inputValue.toLowerCase().trim();
    if (!searchValue) return true;

    const optionData = option.data || option;
    const textValue = optionData[textField];
    if (textValue && String(textValue).toLowerCase().includes(searchValue)) {
      return true;
    }

    const valueValue = optionData[valueField];
    if (valueValue && String(valueValue).toLowerCase().includes(searchValue)) {
      return true;
    }

    return false;
  };

  const styles = useMemo(() => customStyles({ selectGroup }), [selectGroup]);

  return (
    <div className={errorClass}>
      <div data-testid={testIdPrefix}>
        <Select
          value={valueSelected}
          options={data}
          onChange={(val) => {
            lastSelectedRef.current = val;
            if (input) {
              input.onChange(val);
            } else if (onChange) {
              onChange(val);
            }
          }}
          onBlur={() => {
            if (input?.onBlur) {
              input.onBlur(lastSelectedRef.current !== undefined ? lastSelectedRef.current : input.value);
            }
          }}
          getOptionLabel={getOptionLabel}
          formatOptionLabel={formatOptionLabel}
          getOptionValue={(option) => String(option?.[valueField] ?? '')}
          styles={styles}
          placeholder={placeholder}
          defaultValue={defaultValue}
          isSearchable={isSearchable ?? !disabled}
          isDisabled={disabled}
          filterOption={filterOption}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          components={selectComponents}
          valueField={valueField}
          testIdPrefix={testIdPrefix}
          closeMenuOnSelect
          blurInputOnSelect
          openMenuOnFocus={false}
          tabSelectsValue={false}
        />
      </div>
      {meta && <ErrorRender meta={meta} />}
    </div>
  );
};

export default DropdownList;
